/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import { Flex, Spinner } from '@radix-ui/themes'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { practiceTestService } from '@/services/practiceTestService'
import { questionService } from '@/services/questionsService'
import { filterCaseStudyQuestionsByQuestionIds } from '@/utils/practiceTestSectionIds'
import { PracticeTestDetail, PracticeTestStatus } from '@/types/practice-test'
import CreateMockTest from '@/features/create-practice-test'
import MockTestDetail from '@/features/create-practice-test/detail'

export const Route = createFileRoute(
  '/_authenticated/create-practice-test/detail/$ptId/$moduleId'
)({
  component: CreateOrDetailWrapper,
})

function CreateOrDetailWrapper() {
  const { ptId, moduleId } = Route.useParams() as {
    ptId: string
    moduleId: string
    classId: string
  }

  const [page, _setPage] = useState(1)

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['practiceTestDetail', ptId, 'paged', page],
    queryFn: () =>
      practiceTestService.getPracticeTest({
        practice_test_id: ptId,
        size: 10,
        page: page,
      }),
    placeholderData: keepPreviousData,
  })

  const status = pageData?.status?.toLowerCase()?.trim()

  const {
    data: detailPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchTestDetail,
  } = useInfiniteQuery({
    queryKey: ['practiceTestDetailRouter', ptId],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      practiceTestService.getPracticeTest({
        practice_test_id: ptId,
        size: 10,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage) => {
      const sections = lastPage?.sections || []
      // Consider every section's pagination for both questions and case
      // studies, not just sections[0] — otherwise sections with more pages
      // (e.g. Section B/C) stop loading once Section A is exhausted.
      let current = 1
      let total = 1
      for (const s of sections) {
        for (const m of [
          (s?.questions as any)?.meta,
          (s?.case_studies as any)?.meta,
        ]) {
          if (!m) continue
          current = Math.max(current, Number(m.current_page || 1))
          total = Math.max(total, Number(m.total_pages || 1))
        }
      }
      return current < total ? current + 1 : undefined
    },
  })

  const pt = useMemo(() => {
    const pages = (detailPages?.pages || []) as PracticeTestDetail[]
    if (!pages.length) return undefined
    const base = pages[0]
    const toArr = (d: any) => (Array.isArray(d) ? d : d ? [d] : [])
    const bySection: Record<string, PracticeTestDetail['sections'][number]> = {}
    for (const page of pages) {
      for (const s of page.sections || []) {
        const key = s.name
        const qChunk = toArr((s as any)?.questions?.data)
        const csChunk = toArr((s as any)?.case_studies?.data)
        if (!bySection[key]) {
          bySection[key] = {
            ...s,
            questions: {
              ...(s.questions as any),
              data: [...qChunk],
            } as any,
            case_studies: {
              ...(s.case_studies as any),
              data: [...csChunk],
            } as any,
          }
        } else {
          const existing = bySection[key]
          const existingQ = toArr((existing as any)?.questions?.data)
          const existingCs = toArr((existing as any)?.case_studies?.data)
          // Accumulate case studies across pages too, deduping by id —
          // previously only questions were merged, so sections with >10
          // case studies kept only the first page on draft restore.
          const seenCs = new Set(
            existingCs.map((c: any) => c.case_study_id)
          )
          const mergedCs = [
            ...existingCs,
            ...csChunk.filter((c: any) => !seenCs.has(c.case_study_id)),
          ]
          bySection[key] = {
            ...existing,
            questions: {
              ...(existing.questions as any),
              data: [...existingQ, ...qChunk],
            } as any,
            case_studies: {
              ...(existing.case_studies as any),
              data: mergedCs,
            } as any,
          }
        }
      }
    }
    return { ...base, sections: Object.values(bySection) } as PracticeTestDetail
  }, [detailPages])
  const practiceTestDetailData =
    status === PracticeTestStatus.DRAFT ? pt : pageData
  const totalsFromDetail = (() => {
    const sections = practiceTestDetailData?.sections || []
    let totalTime = 0
    let totalMarks = 0
    let totalQuestions = 0
    for (const s of sections) {
      totalTime += s.total_time || 0
      totalMarks += s.total_marks || 0
      totalQuestions += s.total_questions || 0
    }
    return { totalTime, totalMarks, totalQuestions }
  })()

  const { data: draftCaseStudyGroups, refetch: refetchDraftCaseStudyGroups } =
    useQuery({
      queryKey: ['draft-case-study-questions', ptId, pt?.sections, detailPages],
      enabled: status === PracticeTestStatus.DRAFT,
      queryFn: async () => {
        const sections = pt?.sections || []
        const result: Record<string, any[]> = {}
        for (const section of sections) {
          const sectionName = section.name
          const csData = section.case_studies?.data as any
          const caseStudies = Array.isArray(csData)
            ? csData
            : csData
              ? [csData]
              : []
          const groups = await Promise.all(
            caseStudies.map(async (cs: any) => {
              try {
                const resp = await questionService.getQuestionByCaseStudy(
                  cs.case_study_id
                )
                // Normalize list from possible shapes
                const list =
                  (resp as any)?.questions ??
                  (resp as any)?.question ??
                  (resp as any)?.data ??
                  []
                const items = filterCaseStudyQuestionsByQuestionIds(
                  Array.isArray(list) ? list : [],
                  cs.question_ids
                )
                let totalTime = 0
                let totalMarks = 0
                for (const q of items) {
                  totalTime += (q as any).time || 0
                  totalMarks += (q as any).marks || 0
                }
                return {
                  case_study_id: cs.case_study_id,
                  items,
                  total_time: totalTime,
                  total_marks: totalMarks,
                  total_questions: items.length,
                }
              } catch (_err) {
                return {
                  case_study_id: cs.case_study_id,
                  items: [],
                  total_time: cs.total_time ?? cs.total_duration ?? 0,
                  total_marks: cs.total_marks ?? cs.total_mark ?? 0,
                  total_questions: 0,
                }
              }
            })
          )
          result[sectionName] = groups
        }
        return result
      },
    })

  useEffect(() => {
    if (
      status === PracticeTestStatus.DRAFT &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage()
    }
  }, [status, hasNextPage, fetchNextPage, isFetchingNextPage])

  const initialDataForEdit = (() => {
    const sections = pt?.sections || []
    const initialSelectedQuestions: Record<string, any[]> = {}
    const initialSelectedCaseStudies: Record<string, any[]> = {}

    for (const section of sections) {
      const sectionName = section.name
      initialSelectedQuestions[sectionName] =
        (section.questions?.data as any[]) || []
      const csData = section.case_studies?.data as any
      const fallback = Array.isArray(csData) ? csData : csData ? [csData] : []
      initialSelectedCaseStudies[sectionName] =
        (draftCaseStudyGroups as any)?.[sectionName] || fallback
    }

    return { initialSelectedQuestions, initialSelectedCaseStudies }
  })()
  if (isFetching && !pageData) {
    return (
      <Flex
        justify='center'
        align='center'
        className='h-[calc(100vh-200px)] w-full'
      >
        <Spinner />
      </Flex>
    )
  }

  if (status === PracticeTestStatus.DRAFT)
    return (
      <CreateMockTest
        refetchTest={() => {
          refetchTestDetail()
          refetchDraftCaseStudyGroups()
        }}
        initialPracticeTestId={ptId}
        initialPracticeTestName={pt?.pt_name}
        initialGeneralInformation={pt?.general_information}
        status={pt?.status}
        moduleId={moduleId}
        initDuration={pt?.duration}
        initialDurationMinutes={totalsFromDetail.totalTime}
        initialTotalQuestions={totalsFromDetail.totalQuestions}
        initialTotalMarks={totalsFromDetail.totalMarks}
        initialSelectedQuestions={initialDataForEdit.initialSelectedQuestions}
        initialSelectedCaseStudies={
          initialDataForEdit.initialSelectedCaseStudies
        }
        initialReadingTimerEnabled={pt?.reading_timer_enabled}
        initialReadingDurationSeconds={pt?.reading_duration_seconds}
      />
    )
  return <MockTestDetail isLoading={isFetching} />
}

export default Route
